import {
  allowedNextKinds,
  averageSpeed,
  type ClockSchedule,
  ianaOffsetMinutes,
  isDailyKind,
  POSITION_REQUIRED,
  SIDE_KINDS,
  speedLegs,
  steamingHours,
  type VoyageReportKind,
} from '@felix/domain';
import {
  Button,
  Combobox,
  Modal,
  Select,
  Skeleton,
  Table,
  UnstyledButton,
  useCombobox,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconAnchor,
  IconArrowsSort,
  IconBan,
  IconCircleCheckFilled,
  IconClock,
  IconFileText,
  IconGauge,
  IconListDetails,
  IconLock,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconRoute,
  IconRuler2,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useController, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatHours } from '../../format.js';
import { TzOffsetSelect } from '../../inputs/DateTimeLtUtcInput.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell, useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import {
  ClockLogHint,
  defaultReportOffset,
  loadVoyageClock,
  VoyageClockContext,
} from './clock-hint.js';
import {
  CargoProgressSection,
  DailyReferencePanel,
  EtdSection,
  LocationSection,
  RunningHoursSection,
} from './DailySections.js';
import {
  CargoSection,
  ClassificationSection,
  EospReferencePanel,
  FinalCheckSection,
  MooringSection,
  NextPortSection,
  NorSection,
  PassageReviewPanel,
  PilotSection,
  PortCallSection,
  ShipLoadSection,
  VoyageDecisionSection,
} from './DepartureSections.js';
import { hasDirtyFields } from './dirty.js';
import {
  DRAFT_KINDS,
  EVENT_KINDS,
  type EventField,
  type EventFormValues,
  type EventKind,
  eventFields,
  eventToInput,
  eventValues,
  hasSection,
  newEventValues,
} from './event-form.js';
import {
  NoteField,
  NumberField,
  PortField,
  PositionField,
  ScrollTable,
  SelectField,
  TextField,
} from './fields.js';
import { revealErrors, sectionList, useSchemaErrors } from './form-errors.js';
import { FuelLedgerContext, fuelLedgerState, useFuelLedgerStart } from './fuel-ledger.js';
import classes from './LegEventScreen.module.css';
import { EVENT_CATEGORIES, eventAlerts, legGroups } from './leg-event.js';
import { legOf } from './NoonReportScreen.js';
import noon from './NoonReportScreen.module.css';
import {
  formatStamp,
  Group,
  LocalDateTimeField,
  SECTION_ICON,
  SectionTitle,
} from './NoonSections.js';
import type { NextDue } from './next-due.js';
import { nextDue, nextReportPath, onHireSuggested } from './next-due.js';
import { formatLatitude, formatLongitude, formatPlannedTime } from './noon-report.js';
import { useNumber } from './numbers.js';
import {
  type ActionMessage,
  type AlertLine,
  AlertsCard,
  AttachmentsCard,
  LastSubmitted,
  ReportActions,
  ReportTimeCard,
  SideTitle,
} from './RailCards.js';
import { ReopenDialog } from './ReopenDialog.js';
import {
  NextOperationSection,
  PersonsSection,
  RobSection,
  ShipConditionSection,
  TugsSection,
  WeatherSection,
} from './ReportSections.js';
import { useReportAttachments, useReportPrint } from './report-output.js';
import {
  contextMismatch,
  loadVoyageContext,
  shownVoyage,
  UNLINKED,
  type VoyageContext,
} from './screen-load.js';
import type { VoyageSource } from './source.js';
import type { CharterPartyItem, Voyage, VoyageReport, VoyageReportContext } from './types.js';
import { VoidDialog } from './VoidDialog.js';
import {
  ContextMismatchNotice,
  NextDueCard,
  VoyageStartHint,
  VoyageStartNotice,
} from './VoyageContextNotices.js';
import { VoyageHeadCard } from './VoyageHeadCard.js';
import { pendingVoyageStart, startOptions, startsVoyageAfterSubmit } from './voyage-start.js';
import { VoidedNote, WithdrawActions } from './WithdrawActions.js';
import { removableIn } from './withdraw.js';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | ({ kind: 'ready' } & VoyageContext);

/** Tek etiket seti (G4-55): günlükte ve yan kartta aynı kelime; ikon tona göre. */
const STATUS_TONE: Record<string, StatusTone> = {
  draft: 'warning',
  submitted: 'success',
  reopened: 'warning',
  void: 'danger',
  new: 'neutral',
};
const STATUS_ICON = {
  draft: <IconPencil size={13} stroke={2.2} />,
  submitted: <IconCircleCheckFilled size={13} />,
  reopened: <IconRefresh size={13} stroke={2.2} />,
  void: <IconBan size={13} stroke={2.2} />,
  new: <IconClock size={13} stroke={2.2} />,
} as const;

/** "Since Previous Report"ta mesafe ve hız yalnız mesafe alanı olan türlerde (G4-57). */
const hasDistance = (kind: EventKind) =>
  eventFields(kind).some((field) => field.key === 'distanceSinceLast');

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

const systemNow = () => new Date();

/** Varsayılan seçim: açık taslak (ya da yeniden açılmış); yoksa iptal edilmemiş son olay. */
function defaultEvent(events: readonly VoyageReport[]): VoyageReport | null {
  return (
    events.find((r) => r.status === 'draft' || r.status === 'reopened') ??
    events.filter((r) => r.status !== 'void').at(-1) ??
    events.at(-1) ??
    null
  );
}

/** Konum sorgusundan ön seçili yeni olay türü (`?kind=arrival`; Overview hızlı işlemleri, G4-48). */
function kindFromSearch(search: string | undefined): EventKind | null {
  const kind = new URLSearchParams(search ?? '').get('kind');
  return kind && (EVENT_KINDS as readonly string[]).includes(kind) ? (kind as EventKind) : null;
}

export interface LegEventScreenProps {
  source: VoyageSource;
  /** Saat (testlerde sabit); gemi sunucusu yine kendi saatine göre denetler. */
  now?: () => Date;
}

/**
 * Leg / Event (menü "Voyage › Leg / Event"; ADR 0022, ADR 0019). Departure, COSP, EOSP, Arrival,
 * demir, bekleme, sürüklenme, boğaz, kanal, yük ve kiralama olayları tek ekranda: solda bacaklara
 * ayrılmış olay günlüğü, sağda seçilen olayın formu. Görsel referansı yok; dil
 * `sefer-noon-report.jpeg` ve `gemi-sefer-yonetimi.jpeg` ile aynı. Gemide yazılır, gönderilen olay
 * kilitlenir ve kaptan gerekçeyle yeniden açar; ofiste salt okunur.
 */
export function LegEventScreen({ source, now = systemNow }: LegEventScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.event.${key}`, options ?? {});
  const editor = source.editor;
  // All Reports bağlantısı (ADR 0082): `?vessel=` ofiste gemi, `?report=` açılacak olay
  const [vesselId, setVesselId] = useState<string | null>(() =>
    new URLSearchParams(shell?.search ?? '').get('vessel'),
  );
  const reportParam = useRef(new URLSearchParams(shell?.search ?? '').get('report'));
  const [state, setState] = useState<Load>({ kind: 'loading' });
  /** Seçili olay; `null` yeni (kaydedilmemiş) olay ya da seçim yok. */
  const [reportId, setReportId] = useState<string | null>(null);
  const [draftKind, setDraftKind] = useState<EventKind | null>(null);
  const [busy, setBusy] = useState<'save' | 'submit' | 'reopen' | 'remove' | 'void' | null>(null);
  const [message, setMessage] = useState<ActionMessage>(null);
  const [reopening, setReopening] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [preset, setPreset] = useState<EventKind | null>(() => kindFromSearch(shell?.search));
  // Bağlantıdaki sefer (`?voyage=`): planlanan seferin ilk raporu (ADR 0047 §1); `none` sefersiz
  // kalkışlar (ADR 0058 §3). Yükleme bunu ref'ten okur: görünüm değişince seçim korunur
  const voyageParam = useRef(new URLSearchParams(shell?.search ?? '').get('voyage'));
  const [context, setContext] = useState<VoyageReportContext | null>(null);
  // Bağlam okuma hatası ekranda (ADR 0157 §8): boş liste gibi görünmesin
  const [contextError, setContextError] = useState<string | null>(null);
  // Bağlam çelişkisi bildirimi kapatıldı mı ("Bu seferde devam et"; ADR 0157 §3)
  const [stayHere, setStayHere] = useState(false);
  // Geminin CP register'ı (salt okunur): Kiraya Giriş önerisi ve kalkıştaki CP notu (ADR 0157 §7)
  const [charterParties, setCharterParties] = useState<CharterPartyItem[] | null>(null);
  // Gemi saati kaydı: LT farkının varsayılanı ve tutarsızlık uyarısı (ADR 0065 §8)
  const [clock, setClock] = useState<ClockSchedule | null>(null);
  const [applying, setApplying] = useState(false);
  const form = useForm<EventFormValues>({
    defaultValues: {
      kind: 'departure',
      reportedAt: now().toISOString(),
      tzOffsetMin: 0,
      lat: '',
      lon: '',
      data: {},
    },
  });
  // Şema hataları: bayatlayan hata kalkar; gönderimde özet ve ilk hatalı alan (ortak 2026-10-19)
  const schemaErrors = useSchemaErrors(form);
  const mainRef = useRef<HTMLDivElement>(null);
  const [reveal, setReveal] = useState<{ total: number } | null>(null);

  // Gemiler de her okumada yeniden istenir: "Try again" gemiler hatasını da düzeltir (G4-61)
  /** Düzenlenecek rapor (taslak ya da yeniden açılmış, gemide): sürüm 1 kalkış yükseltilir. */
  const editable = useCallback(
    (report: VoyageReport) =>
      !!editor &&
      (report.status === 'draft' || report.status === 'reopened') &&
      (report.can?.edit ?? true) !== false,
    [editor],
  );

  const load = useCallback(
    async (keep?: string | null) => {
      setState({ kind: 'loading' });
      try {
        const loaded = await loadVoyageContext(source, {
          vesselId,
          editing: !!editor,
          voyageId: voyageParam.current,
          reportId: reportParam.current,
          unlinked: true,
        });
        const events = loaded.reports.filter((r) => r.kind !== 'noon').sort(byTime);
        const wanted = keep ?? reportParam.current;
        const shown = (wanted ? events.find((r) => r.id === wanted) : null) ?? defaultEvent(events);
        setReportId(shown?.id ?? null);
        setDraftKind(null);
        if (shown) form.reset(eventValues(shown, { upgrade: editable(shown) }));
        setState({ kind: 'ready', ...loaded });
        // Form bağlamı (gemide): aramalar, açık arızalar, form kayıtları (ADR 0058 §2, §8);
        // hata ekranda gösterilir (ADR 0157 §8)
        if (editor?.context) {
          try {
            setContext(await editor.context());
            setContextError(null);
          } catch (error) {
            setContext(null);
            setContextError(errorCode(error));
          }
        }
      } catch (error) {
        setState({ kind: 'error', code: errorCode(error) });
      }
    },
    [vesselId, source, editor, form, editable],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const vessel = ready?.vessel ?? null;
  useEffect(() => {
    if (!vessel) {
      setClock(null);
      return;
    }
    let live = true;
    void loadVoyageClock(source, vessel).then((value) => {
      if (live) setClock(value);
    });
    return () => {
      live = false;
    };
  }, [source, vessel]);
  useEffect(() => {
    if (!vessel || !editor || !source.charterParties) {
      setCharterParties(null);
      return;
    }
    let live = true;
    source
      .charterParties(vessel.id)
      .then((register) => live && setCharterParties(register.items))
      .catch(() => live && setCharterParties(null));
    return () => {
      live = false;
    };
  }, [source, vessel, editor]);
  const voyage = ready?.voyage ?? null;
  const allReports = ready?.reports ?? [];
  // Hesaplar, sıra ve Next Operation iptal edilmemiş raporlardan; günlük hepsini gösterir (ADR 0077)
  const reports = useMemo(() => allReports.filter((r) => r.status !== 'void'), [allReports]);
  // Geminin zaman çizgisi (ADR 0083 §1): sıra, Next Operation ve "son rapor" sefere bakılmadan
  const timeline = useMemo(
    () => (ready?.timeline ?? []).filter((r) => r.status !== 'void'),
    [ready],
  );
  const unlinkedView = ready?.unlinkedView ?? false;
  /** Görünümü değiştirir: bir sefer, ya da sefersiz kalkışlar (`none`). */
  const showVoyage = (param: string | null, keep?: string | null) => {
    voyageParam.current = param;
    setStayHere(false);
    void load(keep ?? null);
  };
  const events = useMemo(
    () => allReports.filter((r) => r.kind !== 'noon').sort(byTime),
    [allReports],
  );
  const report = draftKind ? null : (events.find((r) => r.id === reportId) ?? null);
  const kind: EventKind | null = draftKind ?? (report?.kind as EventKind | undefined) ?? null;
  // Yakıt defteri (ADR 0117 §2.4, Ek Y3): açılıştan sonraki raporda ROB'un yakıtı defterden
  const watchedAt = useWatch({ control: form.control, name: 'reportedAt' }) as string | undefined;
  // "Gönder ve Seferi Başlat" (ADR 0157 §1, #2261): formdaki sefer kararı
  const watchedAction = useWatch({ control: form.control, name: 'data.voyageAction' }) as unknown;
  const watchedVoyageId = useWatch({ control: form.control, name: 'voyageId' }) as
    | string
    | null
    | undefined;
  const ledgerStart = useFuelLedgerStart(source, vessel?.id ?? null);
  const fuelLedger = fuelLedgerState(
    ledgerStart,
    watchedAt ?? report?.reportedAt ?? null,
    report?.id ?? null,
    vessel?.id ?? null,
    source.scope,
  );
  // Ekler ve ortak PDF (ADR 0057 §3, §7)
  const attachments = useReportAttachments(source, report);
  const printer = useReportPrint(source, { report, reports, voyage, vessel });
  // Next Operation (ADR 0057 §5): geminin son akış raporunun seçimi sıradaki raporu belirler; sefer
  // değişse de, rapor sefersiz de olsa (ADR 0083 §1)
  const allowed = useMemo(() => allowedNextKinds(timeline), [timeline]);
  const readOnly =
    !editor ||
    report?.status === 'submitted' ||
    report?.status === 'void' ||
    (report?.can?.edit ?? true) === false;
  // Taslaktan vazgeçme ve gerekçeli iptal yalnız geminin son akış raporunda (ADR 0077 §3, ADR 0083 §1)
  const removable = report ? removableIn(timeline, report.id) : false;
  // Planlanan sefere de: ilk rapor (Departure ya da seferin ortasından) gemide girilir (ADR 0047 §1)
  // Sefersiz kalkış da gemide girilir (ADR 0058 §3)
  const canCreate =
    !!editor && (unlinkedView || voyage?.status === 'active' || voyage?.status === 'planned');
  // Kaydedilmemiş değişiklik: kabuk üzerinden gezinme onay ister (G4-13)
  const dirty = !readOnly && hasDirtyFields(form.formState.dirtyFields);
  useUnsavedChanges(dirty);
  // Kalkıştan sefere zincir (ADR 0157 §1): gönderilen kalkış; form temiz sayılınca gidilir.
  // Koruma kirli bilgisini etkide günceller: aynı anda gidilirse "kaydedilmemiş değişiklik"
  // sorulurdu (#2278). Bu etki `useUnsavedChanges`'ten sonra çalışır.
  const [chainTo, setChainTo] = useState<string | null>(null);

  const startNew = useCallback(
    (next: EventKind) => {
      if (!vessel) return;
      const at = now();
      // Gemi saati kaydından (yoksa dilimden), yuvarlanmadan (ADR 0065 §8)
      const offset = clock
        ? defaultReportOffset(clock, at.getTime())
        : clockOffsetFallback(vessel.timeZone, at.getTime());
      // Önceki ROB ve saat farkı geminin son raporundan (ADR 0083 §1)
      form.reset(
        newEventValues(next, timeline, at, offset, unlinkedView ? null : voyage, {
          crewOnBoard: context?.crewOnBoard ?? null,
        }),
      );
      setDraftKind(next);
      setReportId(null);
      setMessage(null);
    },
    [vessel, now, form, timeline, voyage, unlinkedView, context, clock],
  );

  // Hızlı işlemden gelen tür (`?kind=`) yüklenince bir kez yeni olay olarak açılır
  useEffect(() => {
    if (!preset || !ready || !canCreate) return;
    setPreset(null);
    startNew(preset);
  }, [preset, ready, canCreate, startNew]);

  const select = (event: VoyageReport) => {
    form.reset(eventValues(event, { upgrade: editable(event) }));
    setDraftKind(null);
    setReportId(event.id);
    setMessage(null);
  };

  const fail = (error: unknown) => {
    setMessage({
      tone: 'danger',
      text: t(`voyage.errors.${errorCode(error)}`, { defaultValue: t('voyage.errors.GENERIC') }),
    });
  };

  /** Kaydeder; formdaki hatalar alanlarda görünür. Gönderimde şemanın tamamı istenir. */
  const save = async (draft: boolean): Promise<VoyageReport | null> => {
    if (!editor || (!voyage && !unlinkedView)) return null;
    schemaErrors.clear();
    const values = form.getValues();
    const conversion = eventToInput(values, { draft, fuelLedger: fuelLedger.active });
    if (!conversion.ok) {
      schemaErrors.apply(conversion.errors);
      setMessage({ tone: 'danger', text: t('voyage.errors.INVALID_VOYAGE_REPORT') });
      // Hatalar ekrana çizilince bölüm bölüm özet ve ilk hatalı alan
      setReveal({ total: conversion.errors.length });
      return null;
    }
    const { kind: _kind, ...fields } = conversion.input;
    // Kalkışın seferi formdaki sefer kararından (ADR 0058 §3); diğer türler görünümün seferinde
    const voyageId = values.kind === 'departure' ? (values.voyageId ?? null) : (voyage?.id ?? null);
    return report
      ? editor.update(report.id, {
          rowVersion: report.rowVersion,
          ...fields,
          ...(values.kind === 'departure' ? { voyageId } : {}),
        })
      : editor.create({ ...conversion.input, voyageId });
  };

  const run = async (
    action: 'save' | 'submit',
    work: () => Promise<VoyageReport | null>,
    done: string,
    then?: (saved: VoyageReport) => boolean,
  ) => {
    setBusy(action);
    setMessage(null);
    try {
      const saved = await work();
      // Zincir (ADR 0157 §1): ekran başka sayfaya geçtiyse yeniden okunmaz
      if (saved && then?.(saved)) return;
      if (saved) {
        // Kalkış başka görünüme geçtiyse (sefersiz ↔ seferli) oraya gidilir
        const here = unlinkedView ? null : (voyage?.id ?? null);
        // Sefere bağlı sefersiz rapor seferinde kalır (ADR 0058 §3, ADR 0083 §2)
        const startsVoyage =
          saved.voyageId === null &&
          ((saved.assignedVoyageId ?? null) === here ||
            (voyage?.departureReportId === saved.id && here !== null));
        if (saved.voyageId !== here && !startsVoyage) {
          voyageParam.current = saved.voyageId ?? UNLINKED;
        }
        await load(saved.id);
        setMessage({ tone: 'success', text: k(done) });
      }
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!reveal) return;
    const frame = requestAnimationFrame(() => {
      const found = revealErrors(mainRef.current, t('voyage.event.title'));
      const hidden = Math.max(0, reveal.total - found.shown);
      setMessage({
        tone: 'danger',
        text: t('voyage.errors.SUMMARY', {
          count: reveal.total,
          sections: sectionList(found.sections, hidden, t('voyage.errors.SUMMARY_OTHER_FIELDS')),
        }),
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [reveal, t]);

  const onSave = () => run('save', () => save(true), 'saved');
  /** Seferi başlatma formu (ADR 0157 §1): kalkıştan dolu; Vazgeç sefersiz kalkışa döner. */
  const startVoyageFrom = (departureId: string) => {
    const back = `/voyage/legs?voyage=${UNLINKED}&report=${departureId}`;
    shell?.navigate(`/voyage/new?departure=${departureId}&back=${encodeURIComponent(back)}`);
  };
  useEffect(() => {
    if (!chainTo || dirty) return;
    setChainTo(null);
    startVoyageFrom(chainTo);
  });
  const onSubmit = () =>
    run(
      'submit',
      async () => {
        const saved = await save(false);
        if (!saved || !editor) return null;
        await editor.submit(saved.id, saved.contentHash);
        return saved;
      },
      'submitted',
      // "Yeni sefer" kararlı sefersiz kalkış: gönderimden sonra sefer formu (ADR 0157 §1, #2260)
      (saved) => {
        if (!source.planner || !shell || !startsVoyageAfterSubmit(saved)) return false;
        // Gönderilen form artık kirli sayılmaz; geçiş koruma güncellenince (etkide) yapılır
        form.reset(form.getValues());
        setChainTo(saved.id);
        return true;
      },
    );

  const onVoid = async (reason: string, password: string | undefined): Promise<string | null> => {
    if (!editor?.voidReport || !report) return null;
    setBusy('void');
    try {
      await editor.voidReport(report.id, {
        contentHash: report.contentHash,
        reason,
        ...(password ? { password } : {}),
      });
      setVoiding(false);
      await load(report.id);
      setMessage({ tone: 'success', text: t('voyage.withdraw.voided') });
      return null;
    } catch (error) {
      return errorCode(error);
    } finally {
      setBusy(null);
    }
  };

  const onRemove = async () => {
    if (!editor || !report) return;
    setBusy('remove');
    try {
      await editor.remove(report.id, report.rowVersion);
      setRemoving(false);
      await load();
      setMessage({ tone: 'success', text: k('removed') });
    } catch (error) {
      setRemoving(false);
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const print = async (mode: 'print' | 'preview') => {
    const failure = await (mode === 'print' ? printer.print() : printer.preview());
    if (failure) setMessage({ tone: 'danger', text: failure });
  };
  const upload = async (file: File) => {
    const code = await attachments.upload(file);
    setMessage(
      code
        ? {
            tone: 'danger',
            text: t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }),
          }
        : { tone: 'success', text: t('voyage.noon.attachments.uploaded', { name: file.name }) },
    );
  };
  // "Next Report": geminin gönderilmiş son akış raporunda izin verilen türler (Noon kendi ekranında)
  const lastFlow = [...timeline]
    .sort(byTime)
    .filter((r) => !SIDE_KINDS.has(r.kind))
    .at(-1);
  const nextReports =
    canCreate && report && report.status === 'submitted' && lastFlow?.id === report.id && allowed
      ? {
          kinds: allowed.kinds.filter((nextKind) => !SIDE_KINDS.has(nextKind)),
          open: (nextKind: VoyageReportKind) => {
            if (nextKind === 'noon') {
              shell?.navigate(`/voyage/noon-report?voyage=${voyage?.id ?? UNLINKED}`);
              return;
            }
            startNew(nextKind as EventKind);
          },
        }
      : null;

  // Sıradaki rapor ve zamanı (ADR 0157 §5): plan Next Operation; düğmeler yalnız formu açar
  const due = useMemo(() => nextDue(timeline, now()), [timeline, now]);
  const onHire = editor ? onHireSuggested(timeline, charterParties ?? []) : false;
  const openNext = canCreate
    ? (nextKind: VoyageReportKind) => {
        const draft = due?.draft;
        if (draft && draft.kind === nextKind) {
          if (draft.kind === 'noon') {
            // Taslak kendi bağlamında: sefersiz taslak planlanan sefere düşmez (#2278)
            shell?.navigate(nextReportPath('noon', draft, voyage?.id ?? null));
            return;
          }
          const found = events.find((r) => r.id === draft.id);
          if (found) select(found);
          else showVoyage(draft.voyageId ?? draft.assignedVoyageId ?? UNLINKED, draft.id);
          return;
        }
        if (nextKind === 'noon') {
          shell?.navigate(nextReportPath('noon', null, voyage?.id ?? null));
          return;
        }
        startNew(nextKind as EventKind);
      }
    : null;
  // Seferi başlatılmamış "Yeni sefer" kalkışı (ADR 0157 §2): kalıcı uyarı
  const pending = editor && ready ? pendingVoyageStart(ready.timeline, ready.voyages) : null;
  /** Kalkışı ve kuyruğunu sefere bağlar (ADR 0157 §4; eski kaynakta seferin kalkış bağı). */
  const linkReports = async (target: Voyage, ids: readonly string[], keep: string) => {
    try {
      if (source.planner?.link) await source.planner.link(target.id, [...ids]);
      else {
        await source.planner?.update(target.id, {
          rowVersion: target.rowVersion,
          departureReportId: keep,
        });
      }
      showVoyage(target.id, keep);
    } catch (error) {
      fail(error);
    }
  };

  // Port Call ATD/ATA önerisi (ADR 0058 §7): gönderilmiş Departure (Cast Off) ve Berthing
  const linkedCall = report?.data.portCall as { id?: string } | undefined;
  const call = context?.portCalls.find((c) => c.id === linkedCall?.id) ?? null;
  const callField =
    report?.kind === 'departure' ? 'atd' : report?.kind === 'arrival' ? 'ata' : null;
  const portCallSuggestion =
    report && report.status === 'submitted' && call && callField && report.schema >= 2
      ? call[callField] === report.reportedAt
        ? null
        : {
            text: t(`voyage.sections.portCall.suggest.${callField}`, {
              port: call.portName,
              at: formatStamp(report.reportedAt),
              current: call[callField] ? formatStamp(call[callField]) : '—',
            }),
            busy: applying,
            onApply: editor?.applyPortCall
              ? () => {
                  setApplying(true);
                  void editor
                    .applyPortCall?.(report.id)
                    .then(async () => {
                      setContext((await editor.context?.().catch(() => null)) ?? null);
                      setMessage({ tone: 'success', text: k('portCallApplied') });
                    })
                    .catch(fail)
                    .finally(() => setApplying(false));
                }
              : null,
          }
      : null;
  // Sefersiz gönderilmiş kalkış: sefer başlat ya da bir sefere bağla (ADR 0058 §3). Kalkış ve
  // ardından gelen sefersiz raporlar birlikte bağlanır; hedefler raporsuz planlanan seferler ve
  // kalkıştan hemen sonra başlayan sefer (ADR 0157 §4, #2269)
  const linkable = (ready?.voyages ?? []).filter(
    (v) => v.status === 'active' || v.status === 'planned',
  );
  const options =
    unlinkedView && editor && report?.kind === 'departure' && report.status === 'submitted'
      ? startOptions(ready?.timeline ?? [], report.id, ready?.voyages ?? [])
      : null;
  const voyageStart =
    options && report
      ? {
          planned: options.targets,
          count: options.queue.length,
          onStart: () => startVoyageFrom(report.id),
          onLink: source.planner
            ? (target: Voyage) =>
                linkReports(
                  target,
                  options.queue.map((r) => r.id),
                  report.id,
                )
            : null,
        }
      : null;

  const reportedAt = form.getValues('reportedAt');
  const header = (
    <PageHeader
      title={k('title')}
      description={editor ? k('description') : k('descriptionOffice')}
      aside={
        vessel ? (
          <VoyageHeadCard
            variant="compact"
            label={unlinkedView ? k('unlinkedTitle') : (voyage?.voyageNo ?? vessel.name)}
            vessel={vessel}
            vesselOptions={source.scope === 'web' ? ready?.vessels : undefined}
            onSelectVessel={(id) => setVesselId(id)}
            facts={[
              { label: t('voyage.noon.header.voyageNo'), value: voyage?.voyageNo ?? '—' },
              {
                label: t('voyage.noon.header.leg'),
                value: voyage
                  ? String(Math.max(1, legOf(reports, reportedAt ?? now().toISOString()))).padStart(
                      2,
                      '0',
                    )
                  : '—',
              },
              { label: t('voyage.noon.header.from'), value: voyage?.fromPort ?? '—' },
              { label: t('voyage.noon.header.to'), value: voyage?.toPort ?? '—' },
            ]}
          />
        ) : state.kind === 'loading' ? (
          <Skeleton height={70} width={560} radius="md" />
        ) : null
      }
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={noon.screen}>
        <div className={noon.main}>
          {header}
          <section className={noon.card}>
            <EmptyState
              tone="danger"
              title={k('error')}
              description={t(`voyage.errors.${state.code}`, {
                defaultValue: t('voyage.errors.GENERIC'),
              })}
              action={
                <Button variant="light" onClick={() => void load()}>
                  {t('common.retry')}
                </Button>
              }
            />
          </section>
        </div>
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div className={noon.screen} aria-busy="true">
        <div className={noon.main}>
          {header}
          <div className={classes.layout}>
            <Skeleton height={420} radius="md" />
            <Skeleton height={560} radius="md" />
          </div>
        </div>
        <aside className={noon.side} aria-label={k('summary')}>
          <ReportTimeCard label={k('eventTime')} at={null} status={null} loading />
          <Skeleton height={180} radius="md" />
        </aside>
      </div>
    );
  }

  if (!voyage && !unlinkedView) {
    return (
      <div className={noon.screen}>
        <div className={noon.main}>
          {header}
          <section className={noon.card}>
            <EmptyState
              icon={<IconRoute size={22} stroke={1.8} />}
              title={t('voyage.noon.noVoyageTitle')}
              description={
                source.scope === 'ship'
                  ? t('voyage.noon.noVoyageShip')
                  : t('voyage.noon.noVoyageOffice')
              }
            />
          </section>
        </div>
      </div>
    );
  }

  // Açık bağlantı son raporun bağlamıyla çelişiyor (ADR 0157 §3): engellemeyen bildirim
  const mismatch =
    !!editor && !stayHere && reportParam.current === null && !!ready && contextMismatch(ready);
  const statusKey = (report ? report.status : 'new') as keyof typeof STATUS_ICON;
  const alerts: AlertLine[] = eventAlerts(events).map((alert, index) => ({
    key: `${alert.code}-${alert.report?.id ?? index}`,
    tone: alert.tone,
    text: k(`alerts.${alert.code}`, {
      kind: alert.report ? t(`voyage.kinds.${alert.report.kind}`) : '',
    }),
    ...(alert.report ? { aside: `${formatStamp(alert.report.reportedAt)} (UTC)` } : {}),
  }));
  // Açık kritik arıza (tel kafes "kritik açık kayıtta uyarı"; ADR 0058 §8) ve sefersiz kalkışlar
  const critical = context?.openDefects.filter((d) => d.critical) ?? [];
  if (critical.length > 0) {
    alerts.unshift({
      key: 'CRITICAL_DEFECTS',
      tone: 'warning',
      text: k('alerts.CRITICAL_DEFECTS', { count: critical.length }),
    });
  }
  const unlinkedCount = ready?.unlinked.length ?? 0;
  if (!unlinkedView && unlinkedCount > 0) {
    alerts.push({
      key: 'UNLINKED_DEPARTURES',
      tone: 'info',
      text: k('alerts.UNLINKED_DEPARTURES', { count: unlinkedCount }),
    });
  }

  return (
    <VoyageClockContext.Provider value={clock}>
      <FormProvider {...form}>
        <div className={noon.screen}>
          <div className={noon.main} ref={mainRef}>
            {header}
            {pending && canCreate ? (
              <VoyageStartNotice
                pending={pending}
                onStart={source.planner ? () => startVoyageFrom(pending.departure.id) : null}
                onLink={
                  source.planner
                    ? (target) =>
                        void linkReports(
                          target,
                          pending.queue.map((r) => r.id),
                          pending.departure.id,
                        )
                    : null
                }
                onOpen={
                  report?.id === pending.departure.id
                    ? null
                    : () => showVoyage(UNLINKED, pending.departure.id)
                }
              />
            ) : null}
            {mismatch && lastFlow ? (
              <ContextMismatchNotice
                last={lastFlow}
                lastVoyageNo={
                  ready?.voyages.find((v) => v.id === ready.continuity)?.voyageNo ?? null
                }
                targetVoyageNo={voyage?.voyageNo ?? null}
                onFollow={() => showVoyage(ready?.continuity ?? UNLINKED)}
                onStay={() => setStayHere(true)}
              />
            ) : null}
            {contextError ? (
              <div className={noon.notice} data-tone="danger" role="alert">
                <IconAlertTriangle size={16} aria-hidden />
                <span>
                  {t('voyage.context.contextError', {
                    error: t(`voyage.errors.${contextError}`, {
                      defaultValue: t('voyage.errors.GENERIC'),
                    }),
                  })}
                </span>
              </div>
            ) : null}
            <div className={classes.layout}>
              <EventLog
                events={events}
                reports={reports}
                selectedId={draftKind ? null : reportId}
                onSelect={select}
                onCreate={canCreate ? startNew : null}
                allowed={allowed?.kinds ?? null}
                unlinked={unlinkedView}
                switcher={
                  unlinkedView ? (
                    (ready?.voyages.length ?? 0) > 0 ? (
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() =>
                          showVoyage(shownVoyage(ready?.voyages ?? [], true)?.id ?? null)
                        }
                      >
                        {k('backToVoyage')}
                      </Button>
                    ) : null
                  ) : unlinkedCount > 0 || canCreate ? (
                    <Button size="xs" variant="subtle" onClick={() => showVoyage(UNLINKED)}>
                      {k('unlinkedLink', { count: unlinkedCount })}
                    </Button>
                  ) : null
                }
              />
              {kind ? (
                <FuelLedgerContext.Provider value={fuelLedger}>
                  <EventForm
                    kind={kind}
                    readOnly={readOnly}
                    locked={report?.status === 'submitted'}
                    next={nextReports}
                    due={nextReports ? due : null}
                    charterParties={charterParties}
                    reports={reports}
                    voyage={voyage}
                    voyages={readOnly ? (ready?.voyages ?? []) : linkable}
                    context={context}
                    currentId={report?.id ?? null}
                    portCallSuggestion={portCallSuggestion}
                    voyageStart={voyageStart}
                  />
                </FuelLedgerContext.Provider>
              ) : (
                <section className={noon.card}>
                  <EmptyState
                    icon={<IconListDetails size={22} stroke={1.8} />}
                    title={canCreate ? k('emptyShip') : k('empty')}
                  />
                </section>
              )}
            </div>
          </div>

          <aside className={noon.side} aria-label={k('summary')}>
            <EventTimeCard
              label={kind ? t(`voyage.kinds.${kind}`) : k('eventTime')}
              status={
                kind
                  ? {
                      tone: STATUS_TONE[statusKey] ?? 'neutral',
                      text: t(`voyage.event.status.${statusKey}`),
                      icon: STATUS_ICON[statusKey],
                    }
                  : null
              }
            />
            {kind ? (
              <LegSummary kind={kind} reports={reports} currentId={report?.id ?? null} />
            ) : null}
            <NextDueCard
              due={due}
              eta={
                voyage ? formatPlannedTime(voyage.plannedEta, voyage.plannedEtaTzOffsetMin) : null
              }
              onOpen={openNext}
              onHire={onHire}
              extra={
                pending && canCreate ? (
                  <VoyageStartHint
                    pending={pending}
                    onStart={source.planner ? () => startVoyageFrom(pending.departure.id) : null}
                  />
                ) : null
              }
            />
            <AlertsCard
              title={t('voyage.noon.alerts.title')}
              lines={alerts}
              empty={k('alerts.none')}
            />
            <AttachmentsCard
              pdfLabel={k('pdf', { kind: kind ? t(`voyage.kinds.${kind}`) : '' })}
              onPrint={report && printer.ready ? () => void print('print') : undefined}
              printing={printer.printing !== null}
              documents={attachments.documents}
              hrefOf={source.attachmentUrl?.bind(source)}
              onUpload={attachments.canUpload ? (file) => void upload(file) : undefined}
              uploadHint={
                editor && kind && !report
                  ? t('voyage.noon.attachments.saveFirst')
                  : editor && report?.status === 'submitted'
                    ? t('voyage.noon.attachments.locked')
                    : null
              }
              uploading={attachments.uploading}
            />
            {editor && kind ? (
              <div className={noon.actionCard}>
                <ReportActions
                  layout="stack"
                  readOnly={readOnly}
                  busy={busy}
                  canReopen={!!report?.can?.reopen}
                  canSubmit={report?.can ? report.can.submit : true}
                  onSave={() => void onSave()}
                  onSubmit={() => void onSubmit()}
                  onReopen={() => setReopening(true)}
                  message={message}
                  submitLabel={
                    source.planner &&
                    kind === 'departure' &&
                    startsVoyageAfterSubmit({
                      kind,
                      voyageId: watchedVoyageId ?? null,
                      assignedVoyageId: report?.assignedVoyageId ?? null,
                      data: { voyageAction: watchedAction },
                    })
                      ? k('submitStart')
                      : k('submit')
                  }
                  {...(report && printer.ready ? { onPrint: () => void print('print') } : {})}
                  printing={printer.printing}
                  lockedNote={
                    report?.status === 'submitted' && !report.can?.reopen
                      ? t('voyage.withdraw.reopenMasterOnly')
                      : null
                  }
                  extra={
                    <WithdrawActions
                      report={report}
                      removable={removable}
                      busy={busy !== null}
                      canVoid={!!editor.voidReport}
                      onRemove={() => setRemoving(true)}
                      onVoid={() => setVoiding(true)}
                    />
                  }
                />
                <LastSubmitted
                  at={report?.submittedAt ?? null}
                  by={report?.submittedByName ?? null}
                />
                {report?.reopenReason && report.status === 'reopened' ? (
                  <p className={noon.last}>
                    {k('reopenedBecause', { reason: report.reopenReason })}
                  </p>
                ) : null}
                <VoidedNote report={report} />
              </div>
            ) : report?.submittedAt ? (
              <>
                <LastSubmitted at={report.submittedAt} by={report.submittedByName} />
                <VoidedNote report={report} />
              </>
            ) : null}
          </aside>
        </div>
        {report && editor ? (
          <ReopenDialog
            opened={reopening}
            onClose={() => setReopening(false)}
            onConfirm={async (reason, password) => {
              setBusy('reopen');
              try {
                await editor.reopen(report.id, {
                  contentHash: report.contentHash,
                  reason,
                  ...(password ? { password } : {}),
                });
                setReopening(false);
                await load(report.id);
                setMessage({ tone: 'success', text: k('reopened') });
                return null;
              } catch (error) {
                return errorCode(error);
              } finally {
                setBusy(null);
              }
            }}
          />
        ) : null}
        {report && editor?.voidReport ? (
          <VoidDialog
            opened={voiding}
            subject="report"
            onClose={() => setVoiding(false)}
            onConfirm={onVoid}
          />
        ) : null}
        <Modal
          opened={removing}
          onClose={() => setRemoving(false)}
          title={k('removeTitle')}
          closeButtonProps={{ 'aria-label': k('cancel') }}
        >
          <p className={noon.muted}>{t('voyage.withdraw.removeBody')}</p>
          <div className={noon.dialogActions}>
            <Button variant="default" onClick={() => setRemoving(false)}>
              {k('cancel')}
            </Button>
            <Button color="red" loading={busy === 'remove'} onClick={() => void onRemove()}>
              {k('remove')}
            </Button>
          </div>
        </Modal>
      </FormProvider>
    </VoyageClockContext.Provider>
  );
}

/** Olay zamanı (UTC) yan kartta: formdaki değer. */
function EventTimeCard({
  label,
  status,
}: {
  label: string;
  status: { tone: StatusTone; text: string; icon: React.ReactNode } | null;
}) {
  const { t } = useTranslation();
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  return (
    <ReportTimeCard
      label={label}
      ariaLabel={t('voyage.event.eventTime')}
      at={reportedAt ?? null}
      status={status}
    />
  );
}

/** Olay ekleme: düğme ve kategorilere ayrılmış, aranabilir liste (G4-51). */
function AddEventMenu({
  onCreate,
  allowed,
}: {
  onCreate: (kind: EventKind) => void;
  /** Next Operation'ın izin verdiği türler (ADR 0057 §5); `null` kısıt yok. */
  allowed: readonly VoyageReportKind[] | null;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => combobox.focusSearchInput(),
  });
  const query = search.trim().toLocaleLowerCase();
  const groups = EVENT_CATEGORIES.map((category) => ({
    ...category,
    kinds: category.kinds.filter(
      (kind) =>
        (allowed === null || allowed.includes(kind)) &&
        t(`voyage.kinds.${kind}`).toLocaleLowerCase().includes(query),
    ),
  })).filter((category) => category.kinds.length > 0);
  return (
    <Combobox
      store={combobox}
      width={260}
      position="bottom-end"
      withinPortal
      onOptionSubmit={(value) => {
        combobox.closeDropdown();
        onCreate(value as EventKind);
      }}
    >
      <Combobox.Target targetType="button">
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => combobox.toggleDropdown()}
        >
          {t('voyage.event.add')}
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('voyage.event.searchKinds')}
          aria-label={t('voyage.event.searchKinds')}
        />
        <Combobox.Options mah={340} style={{ overflowY: 'auto' }}>
          {groups.length === 0 ? (
            <Combobox.Empty>{t('common.noData')}</Combobox.Empty>
          ) : (
            groups.map((category) => (
              <Combobox.Group
                key={category.key}
                label={t(`voyage.event.categories.${category.key}`)}
              >
                {category.kinds.map((kind) => (
                  <Combobox.Option key={kind} value={kind}>
                    {t(`voyage.kinds.${kind}`)}
                  </Combobox.Option>
                ))}
              </Combobox.Group>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

function EventLog({
  events,
  reports,
  selectedId,
  onSelect,
  onCreate,
  allowed,
  unlinked = false,
  switcher = null,
}: {
  events: readonly VoyageReport[];
  reports: readonly VoyageReport[];
  selectedId: string | null;
  onSelect: (event: VoyageReport) => void;
  onCreate: ((kind: EventKind) => void) | null;
  allowed: readonly VoyageReportKind[] | null;
  /** Sefersiz kalkışlar görünümü (ADR 0058 §3). */
  unlinked?: boolean;
  /** Görünüm değiştirme düğmesi (sefer ↔ sefersiz kalkışlar). */
  switcher?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.event.${key}`, options ?? {});
  const n = useNumber();
  const [ascending, setAscending] = useState(true);
  // Dar günlükte (kart 440 px altı; 1280 px pencerede 420) mesafe, telefonda (360 px altı) hız
  // da gizlenir; ikisi formda ve yan kartta da var (G4-02). Böylece olay adı kesilmeden sığar
  // (M6f G7). Genişlik bilinmezken (ilk çizim) bütün sütunlar görünür
  const { ref, width } = useElementSize<HTMLElement>();
  const showDistance = width === 0 || width >= 440;
  const showSpeed = width === 0 || width >= 360;
  const columns = 3 + (showDistance ? 1 : 0) + (showSpeed ? 1 : 0);
  // Hız: önceki rapordan bu yana (Noon dahil, gönderilmiş raporlar; domain hesabı)
  const speeds = useMemo(
    () => new Map(speedLegs(reports).map((leg) => [leg.id, leg.avgSpeedKn])),
    [reports],
  );
  const groups = legGroups(events);
  const ordered = ascending
    ? groups
    : [...groups].reverse().map((group) => ({ ...group, items: [...group.items].reverse() }));
  const groupTitle = (group: (typeof groups)[number]) =>
    group.kind === 'passage'
      ? k(group.open ? 'groups.passageOpen' : 'groups.passage', {
          leg: String(group.leg).padStart(2, '0'),
        })
      : k(`groups.${group.kind}`);
  return (
    <section ref={ref} className={noon.card} aria-label={unlinked ? k('unlinkedLog') : k('log')}>
      <SectionTitle
        icon={<IconListDetails size={SECTION_ICON} />}
        title={unlinked ? k('unlinkedLog') : k('log')}
        aside={
          <span className={classes.logActions}>
            {switcher}
            {onCreate ? <AddEventMenu onCreate={onCreate} allowed={allowed} /> : null}
          </span>
        }
      />
      {unlinked ? <p className={noon.hint}>{k('unlinkedNote')}</p> : null}
      {events.length === 0 ? (
        <EmptyState
          icon={<IconRoute size={22} stroke={1.8} />}
          title={unlinked ? k('noUnlinked') : k('noEvents')}
          description={onCreate ? (unlinked ? k('noUnlinkedShip') : k('noEventsShip')) : undefined}
        />
      ) : (
        <ScrollTable label={k('logTable')}>
          <Table className={[noon.table, classes.table].join(' ')} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  aria-sort={ascending ? 'ascending' : 'descending'}
                  className={classes.colDate}
                >
                  <UnstyledButton
                    className={classes.sort}
                    onClick={() => setAscending((value) => !value)}
                  >
                    {k('when')}
                    {ascending ? (
                      <IconSortAscending size={14} aria-hidden />
                    ) : (
                      <IconSortDescending size={14} aria-hidden />
                    )}
                  </UnstyledButton>
                </Table.Th>
                <Table.Th>{k('eventPosition')}</Table.Th>
                {showDistance ? (
                  <Table.Th data-numeric className={classes.colNum}>
                    {k('distanceNm')}
                  </Table.Th>
                ) : null}
                {showSpeed ? (
                  <Table.Th data-numeric className={classes.colNum}>
                    {t('voyage.overview.speed')}
                  </Table.Th>
                ) : null}
                <Table.Th className={classes.colStatus}>{t('voyage.overview.status')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {ordered.map((group) => [
                <Table.Tr key={group.key} className={classes.groupRow}>
                  <Table.Th colSpan={columns} scope="colgroup">
                    {group.kind === 'passage' ? (
                      <IconRoute size={14} aria-hidden />
                    ) : (
                      <IconAnchor size={14} aria-hidden />
                    )}
                    {groupTitle(group)}
                  </Table.Th>
                </Table.Tr>,
                ...group.items.map((event) => {
                  const stamp = formatStamp(event.reportedAt);
                  const distance = event.data.distanceSinceLast;
                  const selected = event.id === selectedId;
                  const position =
                    event.lat === null || event.lon === null
                      ? '—'
                      : `${formatLatitude(event.lat)}  ${formatLongitude(event.lon)}`;
                  return (
                    <Table.Tr
                      key={event.id}
                      className={classes.row}
                      data-selected={selected || undefined}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => onSelect(event)}
                    >
                      <Table.Td>
                        <span className={classes.when}>
                          {stamp.slice(0, 10)}
                          <small>{stamp.slice(11)}</small>
                        </span>
                      </Table.Td>
                      <Table.Td>
                        <button
                          type="button"
                          className={classes.rowButton}
                          onClick={(click) => {
                            click.stopPropagation();
                            onSelect(event);
                          }}
                        >
                          {t(`voyage.kinds.${event.kind}`)}
                        </button>
                        {event.reportNo ? (
                          <span className={classes.reportNo}>
                            {event.reportNo}
                            {event.amended ? ` · ${k('amended')}` : ''}
                          </span>
                        ) : null}
                        <span className={classes.position} title={position}>
                          {position}
                        </span>
                      </Table.Td>
                      {showDistance ? (
                        <Table.Td data-numeric>
                          {typeof distance === 'number'
                            ? n(distance, { digits: 1, fixed: true })
                            : '—'}
                        </Table.Td>
                      ) : null}
                      {showSpeed ? (
                        <Table.Td data-numeric>
                          {n(speeds.get(event.id) ?? null, { digits: 1, fixed: true })}
                        </Table.Td>
                      ) : null}
                      <Table.Td>
                        <StatusBadge tone={STATUS_TONE[event.status] ?? 'neutral'} variant="dot">
                          {t(`voyage.event.status.${event.status}`)}
                        </StatusBadge>
                      </Table.Td>
                    </Table.Tr>
                  );
                }),
              ])}
            </Table.Tbody>
          </Table>
        </ScrollTable>
      )}
    </section>
  );
}

/** Dilimin farkı (gemi saati kaydı okunamadıysa). */
function clockOffsetFallback(timeZone: string, at: number): number {
  return ianaOffsetMinutes(timeZone, at);
}

function OffsetField({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation();
  const { field } = useController({ name: 'tzOffsetMin' });
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | null;
  // UTC farkı ya da IANA bölge adıyla arama (ADR 0057 §2)
  return (
    <>
      <TzOffsetSelect
        label={t('voyage.noon.navigation.utcOffset')}
        required
        value={Number(field.value ?? 0)}
        at={reportedAt}
        readOnly={readOnly}
        onChange={(value) => field.onChange(value)}
        classNames={{
          input: [noon.input, readOnly ? noon.locked : ''].join(' '),
          label: noon.label ?? '',
          required: noon.required ?? '',
        }}
      />
      {/* Gemi saati kaydıyla tutarsız fark (ADR 0065 §8) */}
      <ClockLogHint
        reportedAt={reportedAt}
        offset={Number(field.value ?? 0)}
        className={noon.hint}
      />
    </>
  );
}

/** Olay zamanı: LT ya da UTC ile girilir, öteki yanında (ADR 0057 §2; G4-52). */
function EventTimeField({ readOnly, label }: { readOnly: boolean; label?: string }) {
  const { t } = useTranslation();
  return (
    <LocalDateTimeField
      name="reportedAt"
      label={label ?? t('voyage.event.eventTime')}
      required
      readOnly={readOnly}
    />
  );
}

function DetailField({ field, readOnly }: { field: EventField; readOnly: boolean }) {
  const { t } = useTranslation();
  const name = `data.${field.key}`;
  const label = t(`voyage.event.fields.${field.key}`);
  if (field.type === 'datetime') {
    return (
      <LocalDateTimeField name={name} label={label} required={field.required} readOnly={readOnly} />
    );
  }
  if (field.type === 'number') {
    return (
      <NumberField
        name={name}
        label={label}
        required={field.required}
        readOnly={readOnly}
        decimals={field.decimals ?? 1}
        {...(field.unit ? { unit: field.unit } : {})}
      />
    );
  }
  if (field.type === 'select' && field.options) {
    const list = field.options.list;
    return (
      <SelectField
        name={name}
        label={label}
        required={field.required}
        readOnly={readOnly}
        options={field.options.values.map((value) => ({
          value,
          label: t(`voyage.event.options.${list}.${value}`),
        }))}
      />
    );
  }
  // Departure ve Arrival limanı listeden seçilir (ADR 0048 §6)
  if (field.key === 'port') {
    return (
      <PortField
        name={name}
        codeName="data.portCode"
        label={label}
        required={field.required}
        readOnly={readOnly}
      />
    );
  }
  return <TextField name={name} label={label} required={field.required} readOnly={readOnly} />;
}

function EventForm({
  kind,
  readOnly,
  locked,
  next,
  reports,
  voyage,
  voyages,
  context,
  currentId,
  portCallSuggestion,
  voyageStart,
  due,
  charterParties,
}: {
  kind: EventKind;
  readOnly: boolean;
  locked: boolean;
  next: { kinds: readonly VoyageReportKind[]; open: (kind: VoyageReportKind) => void } | null;
  /** Sıradaki rapor ve zamanı (ADR 0157 §5). */
  due: NextDue | null;
  /** Geminin CP register'ı: kalkıştaki "Kiralı" notu (ADR 0157 §7). */
  charterParties: readonly CharterPartyItem[] | null;
  reports: readonly VoyageReport[];
  voyage: Voyage | null;
  /** Kalkışın bağlanabileceği seferler (sürmekte ya da planlanan; ADR 0058 §3). */
  voyages: readonly Voyage[];
  context: VoyageReportContext | null;
  currentId: string | null;
  portCallSuggestion: { text: string; onApply: (() => void) | null; busy?: boolean } | null;
  voyageStart: {
    planned: readonly Voyage[];
    count: number;
    onStart: () => void;
    onLink: ((voyage: Voyage) => Promise<void>) | null;
  } | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.event.${key}`);
  const schema = (useWatch({ name: 'schema' }) as number | undefined) ?? 2;
  // Sürüm 2 düzeni (ADR 0058): kalkış ve varış bölümleri; sürüm 1 rapor eski düzeninde
  const v2 = schema >= 2;
  const departure = kind === 'departure' && v2;
  const berthing = kind === 'arrival' && v2;
  const positionRequired = POSITION_REQUIRED.has(kind);
  // Günlükler (ADR 0059): kendi bölümleri; eski tek sayılık tüketim yalnız girilmişse
  const daily = isDailyKind(kind) ? kind : null;
  const all = eventFields(kind, schema);
  const legacyFields = all.filter((f) => f.legacy);
  const legacyValues = useWatch({ name: legacyFields.map((f) => `data.${f.key}`) }) as unknown[];
  const shownLegacy = new Set(
    legacyFields
      .filter((_, i) => legacyValues[i] !== undefined && legacyValues[i] !== null)
      .map((f) => f.key),
  );
  const fields = all.filter((f) => !f.legacy || shownLegacy.has(f.key));
  return (
    <section className={noon.card} aria-label={t(`voyage.kinds.${kind}`)}>
      <SectionTitle icon={<IconFileText size={SECTION_ICON} />} title={t(`voyage.kinds.${kind}`)} />
      {locked ? (
        <div className={noon.notice} data-tone="info">
          <IconLock size={16} aria-hidden />
          <span>{t('voyage.noon.readOnly')}</span>
        </div>
      ) : null}
      {/* Seferi başlat / bağla en üstte (ADR 0157 §4): "Şimdi aç" düğmelerinin altında kalmaz */}
      {voyageStart ? <VoyageStartCard {...voyageStart} /> : null}
      {kind === 'departure' && !v2 ? (
        <div className={noon.notice} data-tone="info">
          <IconLock size={16} aria-hidden />
          <span>{k('legacyDeparture')}</span>
        </div>
      ) : null}
      <div className={classes.groups}>
        <Group title={k('timePosition')}>
          <div className={[noon.pair, classes.fieldPair].join(' ')}>
            <EventTimeField
              readOnly={readOnly}
              {...(departure ? { label: t('voyage.sections.pilot.castOff') } : {})}
            />
            <OffsetField readOnly={readOnly} />
            <PositionField
              name="lat"
              axis="lat"
              label={t('voyage.noon.navigation.latitude')}
              required={positionRequired}
              readOnly={readOnly}
            />
            <PositionField
              name="lon"
              axis="lon"
              label={t('voyage.noon.navigation.longitude')}
              required={positionRequired}
              readOnly={readOnly}
            />
          </div>
        </Group>
        {fields.length > 0 ? (
          <Group title={k('details')}>
            <div className={[noon.pair, classes.fieldPair].join(' ')}>
              {fields.map((field) => (
                <DetailField key={field.key} field={field} readOnly={readOnly} />
              ))}
            </div>
          </Group>
        ) : null}
        {kind === 'eosp' ? (
          <PassageReviewPanel reports={reports} voyage={voyage} currentId={currentId} />
        ) : null}
        {berthing ? <EospReferencePanel reports={reports} currentId={currentId} /> : null}
        {daily ? (
          <DailyReferencePanel kind={daily} reports={reports} currentId={currentId} />
        ) : null}
        {daily ? <LocationSection kind={daily} readOnly={readOnly} /> : null}
        {daily ? <EtdSection readOnly={readOnly} /> : null}
        {daily ? (
          <CargoProgressSection
            kind={daily}
            reports={reports}
            currentId={currentId}
            readOnly={readOnly}
          />
        ) : null}
        {departure || berthing ? (
          <NextPortSection kind={departure ? 'departure' : 'arrival'} readOnly={readOnly} />
        ) : null}
        {departure ? <PilotSection readOnly={readOnly} /> : null}
        {DRAFT_KINDS.has(kind) && !v2 ? (
          <Group title={k('draft')}>
            <div className={[noon.pair, classes.fieldPair].join(' ')}>
              <NumberField
                name="data.draft.fwdM"
                label={k('fields.fwdM')}
                unit="m"
                readOnly={readOnly}
                fixed
              />
              <NumberField
                name="data.draft.aftM"
                label={k('fields.aftM')}
                unit="m"
                readOnly={readOnly}
                fixed
              />
            </div>
          </Group>
        ) : null}
        {departure || berthing ? (
          <ShipLoadSection kind={departure ? 'departure' : 'arrival'} readOnly={readOnly} />
        ) : null}
        {v2 && hasSection(kind, 'nor') ? <NorSection readOnly={readOnly} /> : null}
        {/* Ortak bölümler (ADR 0057 §3): türün `REPORT_SECTIONS` listesine göre */}
        {hasSection(kind, 'tugs') ? <TugsSection readOnly={readOnly} /> : null}
        {berthing ? <MooringSection readOnly={readOnly} /> : null}
        {(departure || berthing) && hasSection(kind, 'cargo') ? (
          <CargoSection kind={departure ? 'departure' : 'arrival'} readOnly={readOnly} />
        ) : null}
        {hasSection(kind, 'weather') ? <WeatherSection readOnly={readOnly} /> : null}
        {hasSection(kind, 'persons') ? <PersonsSection readOnly={readOnly} /> : null}
        {hasSection(kind, 'rob') ? <RobSection readOnly={readOnly} today={!!daily} /> : null}
        {daily ? (
          <RunningHoursSection
            kind={daily}
            reports={reports}
            currentId={currentId}
            readOnly={readOnly}
          />
        ) : null}
        {hasSection(kind, 'shipCondition') ? <ShipConditionSection readOnly={readOnly} /> : null}
        {departure ? (
          <ClassificationSection
            readOnly={readOnly}
            charterParties={readOnly ? null : charterParties}
          />
        ) : null}
        {departure ? <VoyageDecisionSection readOnly={readOnly} voyages={voyages} /> : null}
        {departure ? <FinalCheckSection readOnly={readOnly} context={context} /> : null}
        {departure || berthing ? (
          <PortCallSection readOnly={readOnly} context={context} suggestion={portCallSuggestion} />
        ) : null}
        <Group title={k('remarks')} className={classes.wide}>
          <NoteField name="data.remarks" label={k('remarks')} readOnly={readOnly} minRows={2} />
        </Group>
        <NextOperationSection kind={kind} readOnly={readOnly} next={next} due={due} />
      </div>
    </section>
  );
}

/**
 * Gönderilmiş sefersiz kalkış (ADR 0058 §3): "Start voyage" Add Voyage'ı kalkışla doldurur; "Link"
 * planlanan seferi bu kalkışla başlatır. Raporun içeriği ve hash'i değişmez.
 */
function VoyageStartCard({
  planned,
  count,
  onStart,
  onLink,
}: {
  planned: readonly Voyage[];
  /** Birlikte bağlanacak rapor sayısı: kalkış ve ardından gelen sefersizler (ADR 0157 §4). */
  count: number;
  onStart: () => void;
  onLink: ((voyage: Voyage) => Promise<void>) | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.sections.decision.${key}`);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const chosen = planned.find((v) => v.id === target) ?? null;
  return (
    <Group title={k('startTitle')} className={classes.wide}>
      <p className={noon.hint}>{k('startNote')}</p>
      <div className={classes.startRow}>
        <Button size="sm" leftSection={<IconPlus size={14} />} onClick={onStart}>
          {k('start')}
        </Button>
        {onLink && planned.length > 0 ? (
          <>
            <Select
              aria-label={k('linkPlanned')}
              placeholder={k('linkPlanned')}
              data={planned.map((v) => ({ value: v.id, label: v.voyageNo }))}
              value={target}
              onChange={setTarget}
              size="sm"
            />
            <Button
              size="sm"
              variant="light"
              disabled={!chosen}
              loading={busy}
              onClick={() => {
                if (!chosen) return;
                setBusy(true);
                void onLink(chosen).finally(() => setBusy(false));
              }}
            >
              {`${k('link')} (${count})`}
            </Button>
          </>
        ) : null}
      </div>
    </Group>
  );
}

/**
 * Önceki rapordan bu yana: önceki rapor (iki satır), süre (s:dd), mesafe ve ortalama hız. Mesafe
 * alanı olmayan türlerde mesafe ve hız gizli (G4-57). Hesap domain'de; sunucu aynısını yapar.
 */
function LegSummary({
  kind,
  reports,
  currentId,
}: {
  kind: EventKind;
  reports: readonly VoyageReport[];
  currentId: string | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.event.${key}`);
  const n = useNumber();
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const distance = useWatch({ name: 'data.distanceSinceLast' }) as unknown;
  const previous = reportedAt
    ? reports
        .filter((r) => r.id !== currentId && Date.parse(r.reportedAt) < Date.parse(reportedAt))
        .sort(byTime)
        .at(-1)
    : undefined;
  const hours = previous && reportedAt ? steamingHours(previous.reportedAt, reportedAt) : null;
  const nm = typeof distance === 'number' ? distance : null;
  const speed = nm !== null ? averageSpeed(nm, hours) : null;
  const rows: [typeof IconClock, string, string][] = [
    [IconClock, k('elapsed'), formatHours(hours)],
  ];
  if (hasDistance(kind)) {
    rows.push([IconRuler2, k('distance'), n.unit(nm, 'NM', { digits: 1, fixed: true })]);
    rows.push([IconGauge, k('avgSpeed'), n.unit(speed, 'kn', { digits: 1, fixed: true })]);
  }
  return (
    <section className={noon.sideCard} aria-label={k('summary')}>
      <SideTitle icon={<IconArrowsSort size={22} />} title={k('summary')} />
      <div className={classes.previous}>
        <span className={noon.timeLabel}>{k('previous')}</span>
        {previous ? (
          <>
            <strong>{t(`voyage.kinds.${previous.kind}`)}</strong>
            <span>{`${formatStamp(previous.reportedAt)} (UTC)`}</span>
          </>
        ) : (
          <strong>{k('noPrevious')}</strong>
        )}
      </div>
      <dl className={noon.summaryList} data-plain>
        {rows.map(([Icon, label, value]) => (
          <div key={label}>
            <dt>
              <Icon size={16} aria-hidden className={noon.rowIcon} />
              {label}
            </dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
